import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { AvTelemetryBufferRow } from '../models/av-telemetry-buffer.model';
import { AvTelemetryRecordType } from '../models/av-telemetry.model';

/**
 * Every method fails soft: IndexedDB can be unavailable (private browsing, blocked storage, quota)
 * and telemetry must never break the session it observes.
 */
@Injectable({ providedIn: 'root' })
export class AvTelemetryBufferStore {
  static readonly DB_NAME = 'clearsign-av-telemetry';
  static readonly DB_VERSION = 1;
  static readonly STORE_NAME = 'records';

  static readonly INDEX_EXPIRES_AT = 'byExpiresAt';
  static readonly INDEX_TYPE = 'byType';

  private opening: Promise<IDBDatabase | null> | null = null;

  private readonly rowsWritten = new Subject<void>();

  /** Lets the uploader rebuild its teardown snapshot, which cannot read the store during unload. */
  readonly rowsWritten$: Observable<void> = this.rowsWritten.asObservable();

  async put(rows: readonly AvTelemetryBufferRow[]): Promise<void> {
    if (!rows.length) {
      return;
    }

    const db = await this.open();
    if (!db) {
      return;
    }

    await this.transact(db, 'readwrite', (store) => {
      rows.forEach((row) => store.put(row));
    });

    this.rowsWritten.next();
  }

  async readAll(): Promise<AvTelemetryBufferRow[]> {
    const db = await this.open();
    if (!db) {
      return [];
    }

    return this.request<AvTelemetryBufferRow[]>(db, 'readonly', (store) => store.getAll()) ?? [];
  }

  async count(): Promise<number> {
    const db = await this.open();
    if (!db) {
      return 0;
    }

    return (await this.request<number>(db, 'readonly', (store) => store.count())) ?? 0;
  }

  async readByType(type: AvTelemetryRecordType): Promise<AvTelemetryBufferRow[]> {
    const db = await this.open();
    if (!db) {
      return [];
    }

    const rows = await this.request<AvTelemetryBufferRow[]>(db, 'readonly', (store) =>
      store.index(AvTelemetryBufferStore.INDEX_TYPE).getAll(type)
    );

    return rows ?? [];
  }

  async readExpired(nowMs: number): Promise<AvTelemetryBufferRow[]> {
    const db = await this.open();
    if (!db) {
      return [];
    }

    const rows = await this.request<AvTelemetryBufferRow[]>(db, 'readonly', (store) =>
      store.index(AvTelemetryBufferStore.INDEX_EXPIRES_AT).getAll(IDBKeyRange.upperBound(nowMs))
    );

    return rows ?? [];
  }

  async deleteByIds(ids: readonly number[]): Promise<void> {
    if (!ids.length) {
      return;
    }

    const db = await this.open();
    if (!db) {
      return;
    }

    await this.transact(db, 'readwrite', (store) => {
      ids.forEach((id) => store.delete(id));
    });
  }

  async clear(): Promise<void> {
    const db = await this.open();
    if (!db) {
      return;
    }

    await this.transact(db, 'readwrite', (store) => store.clear());
  }

  private open(): Promise<IDBDatabase | null> {
    if (this.opening) {
      return this.opening;
    }

    this.opening = new Promise<IDBDatabase | null>((resolve) => {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }

      let request: IDBOpenDBRequest;
      try {
        request = indexedDB.open(
          AvTelemetryBufferStore.DB_NAME,
          AvTelemetryBufferStore.DB_VERSION
        );
      } catch {
        resolve(null);
        return;
      }

      request.onupgradeneeded = () => this.createSchema(request.result);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    }).then((db) => {
      if (!db) {
        this.opening = null;
      }

      return db;
    });

    return this.opening;
  }

  private createSchema(db: IDBDatabase): void {
    if (db.objectStoreNames.contains(AvTelemetryBufferStore.STORE_NAME)) {
      return;
    }

    const store = db.createObjectStore(AvTelemetryBufferStore.STORE_NAME, {
      keyPath: 'id',
      autoIncrement: true,
    });

    store.createIndex(AvTelemetryBufferStore.INDEX_EXPIRES_AT, 'expiresAt');
    store.createIndex(AvTelemetryBufferStore.INDEX_TYPE, 'envelope.type');
  }

  private transact(
    db: IDBDatabase,
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => void
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      try {
        const transaction = db.transaction(AvTelemetryBufferStore.STORE_NAME, mode);
        work(transaction.objectStore(AvTelemetryBufferStore.STORE_NAME));

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  private request<TResult>(
    db: IDBDatabase,
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => IDBRequest<TResult>
  ): Promise<TResult | null> {
    return new Promise<TResult | null>((resolve) => {
      try {
        const transaction = db.transaction(AvTelemetryBufferStore.STORE_NAME, mode);
        const request = work(transaction.objectStore(AvTelemetryBufferStore.STORE_NAME));

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        transaction.onabort = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }
}
